import _defineProperty from "@babel/runtime/helpers/defineProperty";
/**
 * Creates a scoped context that automatically qualifies leaf keys with the given scope.
 * Returns leaf keys and clears entries only for keys within this scope.
 *
 * Uses JSON.stringify/parse to serialize/deserialize arbitrarily complex structures.
 * On top of that, handles Date deserialization automatically.
 *
 * NOTE: The AbSDK contains client/webapp/abreact/incubator/ui/utils/LocalStorageUtils
 * but it unfortunately does not use JSON.stringify/parse.
 *
 * @author cweikart
 */
export class ScopedStorageImpl {
  constructor(scope, root = "ScopedStorage") {
    _defineProperty(this, "_scope", void 0);
    if (!scope || !root) {
      throw new Error("ScopedStorageImpl ctor: neither scope nor root can be empty"); // could lead to dangerous situation
    }
    this._scope = [root, scope].join(".");
  }
  get(leafKey) {
    if (!leafKey) {
      throw new Error("ScopedStorageImpl.get: leafKey cannot be empty"); // almost certainly a grievous mistake
    }
    const key = this._qualify(leafKey);
    const serialized = localStorage.getItem(key);
    if (serialized == null)
      // or undefined
      {
        return null;
      }
    const reviver = (keyArg, value) => {
      // detect and deserialize Date
      if (!value) {
        return value;
      }
      const match = value.toString().match(ScopedStorageImpl._dateRegExp);
      return match && match.length === 1 ? new Date(value) : value;
    };
    return JSON.parse(serialized, reviver);
  }
  put(leafKey, value) {
    if (!leafKey) {
      throw new Error("ScopedStorageImpl.put: leafKey cannot be empty"); // almost certainly a grievous mistake
    }
    if (value !== undefined) {
      const key = this._qualify(leafKey);
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } else {
      this.remove(leafKey);
    }
  }
  remove(leafKey) {
    if (!leafKey) {
      throw new Error("ScopedStorageImpl.remove: leafKey cannot be empty"); // almost certainly a grievous mistake
    }
    const key = this._qualify(leafKey);
    localStorage.removeItem(key);
  }
  get keys() {
    const leafKeys = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key && key.startsWith(this._scope)) {
        const leafKey = key.slice(this._scope.length + 1); // 1 === separator length
        leafKeys.push(leafKey);
      }
    }
    return leafKeys;
  }
  clear() {
    this.keys.forEach(key => {
      this.remove(key);
    });
  }
  _qualify(leafKey) {
    return [this._scope, leafKey].join(".");
  }
}
// JSON.stringify serializes Date but JSON.parse does not symmetrically deserialize - ugh.
// So we do that ourselves, within a JSON.parse reviver, when a string matches what Date.toJSON() produces.
// Example: 2020-05-15T04:00:00.000Z
_defineProperty(ScopedStorageImpl, "_dateRegExp", new RegExp("^\\d\\d\\d\\d-\\d\\d-\\d\\dT\\d\\d:\\d\\d:\\d\\d.\\d\\d\\dZ$"));
//# sourceMappingURL=ScopedStorageImpl.js.map
